import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useAccess } from '../context/AccessContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccessPage = () => {
  const navigate = useNavigate();
  const { grantAccess } = useAccess();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showGranted, setShowGranted] = useState(false);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/access`, {
        email,
        age_verified: true // Already verified on Entry page
      });
    } catch (err) {
      console.error('Access error:', err);
    }

    // Brief processing, then show granted
    setTimeout(() => {
      setIsSubmitting(false);
      setShowGranted(true);
    }, 1200);

    // Grant access and redirect smoothly
    setTimeout(() => {
      grantAccess(email);
      navigate('/collection');
    }, 2800);
  };

  // Access Granted screen
  if (showGranted) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-md"
        >
          {/* Checkmark */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.4, type: "spring", delay: 0.1 }}
            className="w-16 h-16 border border-white/20 rounded-full mx-auto mb-8 flex items-center justify-center"
          >
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
            </svg>
          </motion.div>
          
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-zinc-50 mb-4">
            Access granted.
          </h2>
          <p className="font-sans text-sm text-zinc-500">
            Welcome to the collection.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Back Button */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="absolute top-8 left-8"
      >
        <button
          data-testid="back-button"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-sans text-xs tracking-widest uppercase">Back</span>
        </button>
      </motion.div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="w-full max-w-md"
        >
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-serif text-4xl sm:text-5xl font-light text-zinc-50 mb-6">
              Join
            </h1>
            
            {/* Inviting Messaging */}
            <div className="space-y-1">
              <p className="font-sans text-sm text-zinc-400 leading-relaxed">
                Enter your email to access the collection.
              </p>
              <p className="font-sans text-sm text-zinc-500 leading-relaxed">
                Membership is instant and private.
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Email Input */}
            <div>
              <label className="block font-sans text-xs text-zinc-600 tracking-wide uppercase mb-3">
                Email Address
              </label>
              <input
                type="email"
                data-testid="email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                autoFocus
                className="w-full bg-transparent border-b border-zinc-800 focus:border-white py-4 text-white font-sans text-base placeholder:text-zinc-600 transition-colors duration-300 outline-none"
              />
            </div>

            {/* Error Message */}
            {error && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-400/80 text-sm font-sans"
                data-testid="error-message"
              >
                {error}
              </motion.p>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              data-testid="continue-button"
              disabled={isSubmitting}
              className="w-full bg-white text-black py-4 font-sans text-xs font-medium tracking-[0.2em] uppercase hover:bg-zinc-200 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
              ) : (
                'Continue'
              )}
            </button>
          </form>

          {/* Privacy Note */}
          <p className="text-center text-zinc-600 text-xs font-sans mt-8">
            Your information is kept private and secure.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default AccessPage;
